/**
 * CorrespondenceUIContext
 *
 * Minimal context containing ONLY pure UI state.
 * All server state (package data, transmission, validation, components)
 * should be accessed via the RTKQ hooks directly:
 *   - usePackageData
 *   - usePackageTransmission
 *   - usePackageValidation
 *   - usePackageComponents
 *
 * This follows modern React/RTKQ best practices:
 * - Server state lives in RTKQ cache (single source of truth)
 * - Context only for UI state that needs to be shared
 * - Components subscribe directly to RTKQ hooks with selectFromResult
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import type {
  CorrespondencePackageType,
  RecipientEntityType,
} from '@/services/api/correspondenceService';

import type { PackageMode } from '../types';

// ============================================================================
// Types
// ============================================================================

interface ModalState {
  upload: boolean;
  send: boolean;
  clientInfo: boolean;
  eSign: boolean;
}

/** Focus targets for scroll-to-view actions */
type FocusTarget = 'recipient' | 'transmission' | null;

type ModalAction =
  | { type: 'OPEN_MODAL'; modal: keyof ModalState }
  | { type: 'CLOSE_MODAL'; modal: keyof ModalState }
  | { type: 'CLOSE_ALL_MODALS' };

interface CorrespondenceUIContextValue {
  // Package identification (needed by child hooks)
  matterId: string;
  packageId: string | undefined;
  packageType: CorrespondencePackageType;

  // Initial recipient (for package creation - only used by main workspace)
  initialRecipientEntityType: RecipientEntityType | undefined;
  initialRecipientEntityId: string | undefined;
  coverageId: string | undefined;
  withDefaults: boolean;

  // Pure UI state
  packageMode: PackageMode;
  setPackageMode: (mode: PackageMode) => void;
  selectedComponentId: string | null;
  setSelectedComponentId: (id: string | null) => void;

  // Focus management (for scroll-to-view from validation banner)
  focusTarget: FocusTarget;
  setFocusTarget: (target: FocusTarget) => void;

  // Modal management
  modals: ModalState;
  openModal: (modal: keyof ModalState) => void;
  closeModal: (modal: keyof ModalState) => void;
  closeAllModals: () => void;

  // Package ID management (set by main workspace after creation)
  setPackageId: (id: string) => void;
}

// ============================================================================
// Reducers
// ============================================================================

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return { ...state, [action.modal]: true };
    case 'CLOSE_MODAL':
      return { ...state, [action.modal]: false };
    case 'CLOSE_ALL_MODALS':
      return { upload: false, send: false, clientInfo: false, eSign: false };
    default:
      return state;
  }
};

// ============================================================================
// Context
// ============================================================================

const CorrespondenceUIContext =
  createContext<CorrespondenceUIContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface CorrespondenceUIProviderProps {
  matterId: string;
  packageId?: string;
  packageType: CorrespondencePackageType;
  recipientEntityType?: RecipientEntityType;
  recipientEntityId?: string;
  coverageId?: string;
  withDefaults?: boolean;
  children: ReactNode;
}

export const CorrespondenceUIProvider = ({
  matterId,
  packageId: initialPackageId,
  packageType,
  recipientEntityType,
  recipientEntityId,
  coverageId,
  withDefaults = false,
  children,
}: CorrespondenceUIProviderProps) => {
  // Package ID - either from props or set after creation by main workspace
  const [createdPackageId, setCreatedPackageId] = useState<string | null>(null);
  const packageId = initialPackageId || createdPackageId || undefined;

  // Setter for main workspace to update after creation
  const setPackageId = useCallback(
    (id: string) => {
      if (!initialPackageId) {
        setCreatedPackageId(id);
      }
    },
    [initialPackageId],
  );

  // ======== UI State ========
  const [packageMode, setPackageModeState] = useState<PackageMode>('view');
  const [hasInitializedMode, setHasInitializedMode] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(
    null,
  );

  // Modal state
  const [modals, dispatchModals] = useReducer(modalReducer, {
    upload: false,
    send: false,
    clientInfo: false,
    eSign: false,
  });

  // Focus state (for scroll-to-view from validation banner CTAs)
  const [focusTarget, setFocusTargetState] = useState<FocusTarget>(null);

  const setFocusTarget = useCallback((target: FocusTarget) => {
    setFocusTargetState(target);
    // Auto-clear after a short delay to allow consuming component to react
    if (target) {
      setTimeout(() => setFocusTargetState(null), 100);
    }
  }, []);

  // ======== Mode Management ========
  // Note: canEdit check needs to be done by consuming component
  // since we don't have packageData here
  const setPackageMode = useCallback((mode: PackageMode) => {
    setPackageModeState(mode);
  }, []);

  // Initialize mode when we get a packageId
  useEffect(() => {
    if (!hasInitializedMode && packageId) {
      // Default to edit mode for new packages
      setPackageModeState('edit');
      setHasInitializedMode(true);
    }
  }, [packageId, hasInitializedMode]);

  // ======== Modal Actions ========
  const openModal = useCallback((modal: keyof ModalState) => {
    dispatchModals({ type: 'OPEN_MODAL', modal });
  }, []);

  const closeModal = useCallback((modal: keyof ModalState) => {
    dispatchModals({ type: 'CLOSE_MODAL', modal });
  }, []);

  const closeAllModals = useCallback(() => {
    dispatchModals({ type: 'CLOSE_ALL_MODALS' });
  }, []);

  // ======== Context Value ========
  const value: CorrespondenceUIContextValue = useMemo(
    () => ({
      // Package identification
      matterId,
      packageId,
      packageType,

      // Initial values for package creation (only used by main workspace)
      initialRecipientEntityType: recipientEntityType,
      initialRecipientEntityId: recipientEntityId,
      coverageId,
      withDefaults,

      // UI state
      packageMode,
      setPackageMode,
      selectedComponentId,
      setSelectedComponentId,

      // Focus management
      focusTarget,
      setFocusTarget,

      // Modals
      modals,
      openModal,
      closeModal,
      closeAllModals,

      // Package ID management
      setPackageId,
    }),
    [
      matterId,
      packageId,
      packageType,
      recipientEntityType,
      recipientEntityId,
      coverageId,
      withDefaults,
      packageMode,
      setPackageMode,
      selectedComponentId,
      focusTarget,
      setFocusTarget,
      modals,
      openModal,
      closeModal,
      closeAllModals,
      setPackageId,
    ],
  );

  return (
    <CorrespondenceUIContext.Provider value={value}>
      {children}
    </CorrespondenceUIContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useCorrespondenceUI = () => {
  const context = useContext(CorrespondenceUIContext);
  if (!context) {
    throw new Error(
      'useCorrespondenceUI must be used within CorrespondenceUIProvider',
    );
  }
  return context;
};
